import React, { useEffect, useState } from 'react';
import { useIr, useNewFunderRating } from 'shared/apollo';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Beneficiaries from './Beneficiaries';
import Budget from './Budget';
import Button from '@mui/material/Button';
import Header from './Header';
import { IrCustomQuestions } from 'shared/components/IrCustomQuestions';
import { IrDonorClaim } from 'shared/components/IrDonorClaim';
import { IrImpactModel } from 'shared/components/IrImpactModel';
import { IrRatings } from 'shared/components/IrRatings';
import { IrTags } from 'shared/components/IrTags';
import IrPortion from './IrPortion';
import Narratives from './Narratives';
import PrintIcon from '@mui/icons-material/Print';
import Services from './Services';
import Timeline from './Timeline';
import WarningIcon from '@mui/icons-material/Warning';
import { gql } from 'apollo-boost';
import styled from 'styled-components';
import PrinterSvg from 'shared/statics/svg/Printer.svg';

export const ImpactReceipt = ({
  investmentId,
  backgroundColor = '#EAF0F6',
  isNpo = false,
}) => {
  const [data, setData] = useState({});
  const [invalidIr, setInvalidIr] = useState(false);
  const { data: query, error: hasError, isLoading } = useIr(investmentId);
  const { run: runNew } = useNewFunderRating();
  const ready = !isLoading && !invalidIr;

  useEffect(() => {
    if (query && query.report) {
      setData(query);
    }
  }, [query]);

  useEffect(() => {
    if (hasError) setInvalidIr(true);
  }, [hasError]);

  const handleOnPressRate = (
    value,
    reportId,
    funderRatingCategoryId,
    index,
    isSecond,
  ) => {
    const newData = { ...data };
    const newReport = { ...newData.report };
    const half = Math.floor(newReport.funder_rating_categories.length / 2);
    const updateIndex = isSecond ? half + index : index;
    newReport.funder_rating_categories[updateIndex]['current_value'] = `${value}`;
    runNew({ value, reportId, funderRatingCategoryId });
  };

  const togglePrintMode = add => {
    ['header.app-header', '.print-btn'].forEach(selector => {
      document
        .querySelector(selector)
        ?.classList[add ? 'add' : 'remove']('print-mode');
    });
  };

  const handleOnPrint = () => {
    togglePrintMode(true);
    window.print();
  };

  window.onafterprint = function() {
    console.log('Printing completed...');
    togglePrintMode(false);
  };

  const questions = data?.report?.custom_question_responses;
  const hasQuestions = !!(questions && Object.keys(questions));
  const hasRatings = data?.report?.funder_rating_categories;

  return (
    <Root>
      <Container color={backgroundColor}>
        <Col className="top">
          <Row>
            <ArrowBackIcon />
            <span className="title">
              Impact Receipt <sup>®</sup>
            </span>
          </Row>
          {ready && (
            <Row>
              <Button
                className="print-btn"
                onClick={handleOnPrint}
                variant="outlined"
                color="primary"
                startIcon={<PrintIcon />}
              >
                Print View
              </Button>
            </Row>
          )}
        </Col>
        {invalidIr && (
          <Col>
            <InvalidContainer>
              <span>
                <WarningIcon />
              </span>
              <span className="main">You're not supposed to be here</span>
              <span>The requested Impact Receipt does not exist</span>
            </InvalidContainer>
          </Col>
        )}
        {ready && (
          <Col>
            <Header {...{ data, isNpo }} />
          </Col>
        )}
        {ready && (
          <Row>
            <Col>
              <Timeline {...{ data }} />
            </Col>
          </Row>
        )}
        {ready && !isNpo && (
          <Col>
            <Row>
              <IrPortion
                invested={data.invested}
                budget={
                  parseFloat(
                    data?.report?.budget?.find(b => b.expenses === 'Total Program Cost')?.current ?? 0
                  )
                }
              />
            </Row>
            <Row>
              <IrDonorClaim data={data?.report?.donors_claim || []} />
            </Row>
          </Col>
        )}
        {ready && data?.report?.tags?.length > 0 && (
          <Col>
            <Row>
              <IrTags data={data?.report?.tags} />
            </Row>
          </Col>
        )}
      </Container>
      {ready && (
        <Container color="#f9f9f9">
          <Col>
            <Row>
              <Services data={data?.report} />
            </Row>
            <Row>
              <Beneficiaries data={data?.report} />
            </Row>
          </Col>
          <Col>
            <Row>
              <IrImpactModel impactModel={data?.report?.impact_model} />
            </Row>
          </Col>
          {hasRatings && (
            <Col>
              <Row>
                <IrRatings
                  data={data?.report?.funder_rating_categories}
                  reportId={data?.report?.id}
                  handleOnPressRate={handleOnPressRate}
                />
              </Row>
            </Col>
          )}
          {hasQuestions && questions && Object.keys(questions).length > 0 && (
            <Col>
              <Row>
                <IrCustomQuestions
                  data={data?.report?.custom_question_responses}
                />
              </Row>
            </Col>
          )}
          <Col>
            <Row>
              <Narratives data={data?.report} />
            </Row>
          </Col>
          <Col>
            <Row>
              <Budget data={data?.report} />
            </Row>
          </Col>
        </Container>
      )}
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  max-width: 1280px;
  margin-left: 50%;
  transform: translateX(-50%);
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ color }) => color};
  padding: 20px;
`;

const InvalidContainer = styled.div`
  width: 100%;
  height: calc(100vh - 158px);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  svg {
    font-size: 120px;
    color: #0073a3;
  }
  span.main {
    margin-left: 30px;
    font-family: Roboto;
    font-size: 24px;
    color: #0073a3;
    font-weight: bold;
  }
  span {
    margin-left: 30px;
    font-family: Roboto;
    font-size: 16px;
    color: #595959;
  }
`;

const Col = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  &.top {
    div {
      color: #0073a3;
      span.title {
        margin-left: 30px;
        font-family: Roboto;
        font-size: 32px;
        color: #595959;
      }
      sup {
        font-size: 16px;
      }
      button {
        margin-top: 10px;
        float: right;
        text-transform: none !important;
      }
    }
  }
`;

const Row = styled.div`
  flex: 1;
  padding: 10px;
  .print-btn {
    .MuiButton-startIcon {
      &:before {
        content: '';
        width: 23px;
        height: 20px;
        background-size: cover;
        background-image: url(${PrinterSvg});
      }
      .MuiSvgIcon-root {
        display: none;
      }
    }
  }
`;


export const investmentById = gql`
  query($id: ID) {
    allInvestments(filters: { id: $id }) {
      edges {
        node {
          grantee {
            officeLocation
          }
        }
      }
    }
  }
`;
